<template>
  <div
    ref="pageWrapperRef"
    :class="{
      'cdi-PageWrapper-is-dragging': customDashboardState.isDragging
    }"
    class="cdi-PageWrapper"
    data-auto-testid="custom-dashboard-item-page"
  >
    <DashboardItemLoader v-if="loading" :hide-actions="hideActions" :hide-title="hideTitle" />
    <div v-else-if="isDashboardUnavailable" class="cdi-EmptyState">
      <DashboardAccessDenied hide-back-button>
        <template #title> {{ $t('homepage.dashboard.not_available') }} </template>
      </DashboardAccessDenied>
    </div>
    <div v-else class="cdi-Wrapper">
      <div class="cdi-Header">
        <div class="cdi-Header_Left" data-export-ignore>
          <template v-if="!hideActions">
            <AppButton
              class="cdi-Header-Button"
              height="24"
              icon="arrow-left-next"
              style="--button-high-padding: 8px"
              type="link"
              width="24"
              @click="backToCustomDashboardList"
            >
              <div class="cdi-Header-Button-lightGrey">
                {{ $t('dashboard.menu.custom_dashboard', 2) }}
              </div>
            </AppButton>
            <span>/</span>
            <span class="cdi-BreadcrumbCurrentName oboard-truncated-text">
              {{ dashboardItem.name }}
            </span>
          </template>
        </div>
        <div class="cdi-Header_Right">
          <template v-if="!hideActions">
            <AppButton
              v-tippy="{
                content: $t('objective.chart.fullscreen'),
                placement: 'bottom'
              }"
              :icon="fullscreenIcon"
              class="cdi-Header-Button"
              data-export-ignore
              remove-padding
              size="sm"
              type="ghost-next"
              @click="onFullscreenClick"
            />
            <AppDivider data-export-ignore vertical />
            <AppButton
              v-tippy="{
                content: $t(
                  dashboardModel.favorite
                    ? 'action.remove_from_favorites'
                    : 'action.add_to_favorites'
                ),
                placement: 'bottom',
                theme: DROP_LIST_THEMES.TRANSLUCENT_NEXT
              }"
              :class="{ 'cdi-ButtonFavorite-active': dashboardModel.favorite }"
              :disable="isLoadingFavoriteToggle"
              :icon="dashboardModel.favorite ? 'favorite-filled' : 'add-to-favorites-black'"
              class="cdi-Header-Button"
              data-export-ignore
              height="24"
              remove-padding
              size="sm"
              type="ghost-next"
              width="24"
              @click="toggleFavorite"
            />
            <AppButton
              v-if="userCanManageCustomDashboard"
              v-tippy="{
                content: $t('action.share'),
                placement: 'bottom',
                theme: DROP_LIST_THEMES.TRANSLUCENT_NEXT
              }"
              class="cdi-Header-Button"
              data-export-ignore
              height="24"
              icon="share"
              remove-padding
              size="sm"
              type="ghost-next"
              width="24"
              @click="modalVisibilityState[SHOW_SHARING_MODAL] = true"
            />
            <AppAvatar
              v-tippy="{
                content: $t('dashboard.created_by', { userName: getOwner.ownerName }),
                placement: 'bottom',
                theme: DROP_LIST_THEMES.TRANSLUCENT_NEXT
              }"
              :avatar-url="getOwner.ownerAvatarUrl"
              no-margins
            />
          </template>
          <AppButton
            v-if="userCanManageCustomDashboard"
            v-tippy="{
              content: isModuleLimitReached ? $t('dashboard.module_limit_reached') : null
            }"
            :disable="isModuleLimitReached"
            data-export-ignore
            icon="plus-next"
            @click="modalVisibilityState[SHOW_CHOOSE_MODULE_DIALOG] = true"
          >
            {{ $t('dashboard.add_module') }}
          </AppButton>
        </div>
      </div>
      <div v-if="!hideTitle" class="cdi-Title">
        <OkrTitleFiled
          v-model="dashboardModel.name"
          :is-error="isTitleEmpty"
          :max-length="CUSTOM_DASHBOARD_NAME_MAX_LENGTH"
          :placeholder="$t('common.title')"
          :readonly="!userCanManageCustomDashboard || customDashboardState.isDragging"
          data-auto-testid="name-field"
          data-testid="name-field"
          @blur="onNameUpdate"
          @update:model-value="isTitleEmpty = isStringEmpty($event)"
        />
      </div>

      <div
        :ref="modulesReferenceKey"
        :class="{
          'cdi-ModuleWrapper-is-dragging': customDashboardState.isDragging,
          'cdi-ModuleWrapper-no-edit': !userCanManageCustomDashboard,
          'cdi-ModuleWrapper-no-modules': isNoModules
        }"
        class="cdi-ModuleWrapper"
      >
        <div v-if="isNoModules" class="cdi-EmptyState">
          <SettingsPagesEmptyState hero-height="128" hero-icon="no-groups-hero" hero-width="142">
            <template #title> {{ $t('dashboard.no_module_yet') }} </template>
            <template #subtitle> {{ $t('dashboard.you_dont_have_modules') }} </template>

            <template v-if="userCanManageCustomDashboard" #action>
              <AppButton
                height="24"
                icon="plus-next"
                type="primary-next"
                width="24"
                @click="modalVisibilityState[SHOW_CHOOSE_MODULE_DIALOG] = true"
              >
                {{ $t('dashboard.add_module') }}
              </AppButton>
            </template>
          </SettingsPagesEmptyState>
        </div>

        <template v-else>
          <VirtualizationList
            :data="dashboardModel.modules"
            data-key="id"
            fallback-on-body
            item-class="cdi-ModuleItem"
            @drag-start="onDragStart"
            @drag-end="onDragEnd"
          >
            <template #default="{ item }">
              <CustomDashboardModuleResolver :module="item">
                <template #default="{ component }">
                  <div
                    :id="getCustomDashboardModuleSelectorId(item.id)"
                    :data-auto-testid="getCustomDashboardModuleTestId(item)"
                  >
                    <component
                      :is="component"
                      v-model:layout-type-id="item.layoutTypeId"
                      :can-edit="userCanManageCustomDashboard"
                      :dashboard-item="dashboardItem"
                      :form-model="item"
                    />
                  </div>
                </template>
              </CustomDashboardModuleResolver>
            </template>
          </VirtualizationList>
        </template>
      </div>
    </div>
  </div>

  <portal to="modal-windows">
    <EditModule
      v-model:show-modal="modalVisibilityState[SHOW_EDIT_MODULE_MODAL]"
      :dashboard-item="dashboardItem"
      :model-value="moduleToManage"
      @open-add-objective-modal="modalVisibilityState[SHOW_ADD_OBJECTIVES_MODAL] = true"
      @update-list="reloadDashboard"
      @module-default-view-updated="reloadDashboard({ ignoreCurrentlyEditableModule: true })"
      @module-default-column-count-updated="
        reloadDashboard({ ignoreCurrentlyEditableModule: true })
      "
    />
    <AddObjectiveModal
      :dashboard-item="dashboardItem"
      :model-value="moduleToManage"
      :show="modalVisibilityState[SHOW_ADD_OBJECTIVES_MODAL]"
      @on-close="hideModal(SHOW_ADD_OBJECTIVES_MODAL)"
      @on-success="reloadDashboard"
    />

    <DuplicateCustomDashboardModule
      v-model:show="modalVisibilityState[SHOW_DUPLICATE_MODAL]"
      :dashboard-to-duplicate="dashboardItem"
      :model-value="moduleToManage"
      @confirm="onConfirmDuplicate"
    />
    <RemoveCustomDashboard
      :dashboard-for-delete="dashboardModel"
      :show-confirm-delete-dialog="modalVisibilityState[SHOW_CONFIRM_DELETE_DIALOG]"
      @close="hideModal(SHOW_CONFIRM_DELETE_DIALOG)"
      @on-success="onSuccessRemoveDashboard"
    />
    <RemoveCustomDashboardModule
      :dashboard-item="dashboardItem"
      :module-to-delete="moduleToManage"
      :show-confirm-delete-dialog="modalVisibilityState[SHOW_CONFIRM_DELETE_MODULE_DIALOG]"
      @close="hideModal(SHOW_CONFIRM_DELETE_MODULE_DIALOG)"
      @on-success="reloadDashboard"
    />
    <ShareCustomDashboard
      v-model:show="modalVisibilityState[SHOW_SHARING_MODAL]"
      :can-edit="userCanManageCustomDashboard"
      :can-edit-owner="isOwner || isPluginAdmin"
      :dashboard-for-share="dashboardItem"
      @on-confirm="reloadDashboard"
    />
    <ChooseCustomDashboardModulesDialog
      v-model:show="modalVisibilityState[SHOW_CHOOSE_MODULE_DIALOG]"
      :item="dashboardItem"
      @created="reloadDashboard"
    />

    <ObjectiveModal
      v-if="modalVisibilityState[SHOW_OBJECTIVE_MODAL]"
      ref="objectiveModal"
      :workspace-id="resolvedWorkspaceId"
      source="custom-dashboard"
      @close="onElementsUpdated"
      @closed="hideModal(SHOW_OBJECTIVE_MODAL)"
      @element-created="emit('elements-updated')"
    />

    <ModuleColumnsSettingsModal
      v-if="userCanManageCustomDashboard"
      :enabled-columns="customDashboardState.enabledColumns"
      :module-id="customDashboardState.columnsSettingsModuleId"
      @close="customDashboardState.toggleModuleColumnsSettings"
      @update-enabled-columns="updateModuleEnabledColumns"
    />

    <CheckinModal
      v-if="isShowCheckinModal"
      v-model="isCheckinModalOpen"
      :entry-point="checkinModalLaunchData.entryPoint"
      :okr-element="checkinModalLaunchData.okrElement"
      :preset-backdate="checkinModalLaunchData.presetBackdate"
      :tracking-source="EVENT_SOURCES.CUSTOM_DASHBOARD"
      @saved="onElementsUpdated"
      @after-leave="onCheckinModalClosed"
    />
  </portal>
</template>

<script setup>
import { cloneDeep, isArray, isEmpty, isNil, isNull } from 'lodash'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  shallowRef,
  useTemplateRef,
  watch
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import LevelApiHandler from '@/api/level'
import { useVerticalDragLock } from '@/composables/vertical-drag-lock'
import { useWorkspacesLevels } from '@/composables/workspaces-levels'
import { ROUTE_NAMES } from '@/routes/route-helpers'
import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { useCheckinModal } from '@/utils/check-ins/check-ins'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import {
  CUSTOM_DASHBOARD_ITEM_TYPES,
  CUSTOM_DASHBOARD_NAME_MAX_LENGTH,
  getCustomDashboardModuleSelectorId,
  getDashboardState,
  MODULE_LIMIT,
  MODULE_TYPE_IDS,
  useCustomDashboardModuleHelper
} from '@/utils/custom-dashboard-helper'
import { handleOrder } from '@/utils/drag-n-drop'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import {
  customDashboardModulesStateInjectionKey,
  customDashboardStateInjectionKey
} from '@/utils/injection-keys'
import { OKR_TYPE_TO_FORM_VIEW } from '@/utils/objectives'
import { getEditValueFromQuery } from '@/utils/okr-elements-forms-helpers'
import { includesValue } from '@/utils/prop-validators'
import { checkResponseStatus, RESPONSE_STATUSES } from '@/utils/response-statuses'

import DashboardItemLoader from '@/components/dashboard/custom-dashboard/DashboardItemLoader'
import AddObjectiveModal from '@/components/dashboard/custom-dashboard/modals/AddObjectiveModal'
import ChooseCustomDashboardModulesDialog from '@/components/dashboard/custom-dashboard/modals/ChooseCustomDashboardModulesDialog'
import DuplicateCustomDashboardModule from '@/components/dashboard/custom-dashboard/modals/DuplicateCustomDashboardModule'
import EditModule from '@/components/dashboard/custom-dashboard/modals/EditModule'
import RemoveCustomDashboard from '@/components/dashboard/custom-dashboard/modals/RemoveCustomDashboard'
import RemoveCustomDashboardModule from '@/components/dashboard/custom-dashboard/modals/RemoveCustomDashboardModule'
import ShareCustomDashboard from '@/components/dashboard/custom-dashboard/modals/ShareCustomDashboard'
import CheckinModal from '@/components/objectives/element-check-in/CheckinModal'
import OkrTitleFiled from '@/components/objectives/forms/OkrTitleFiled'
import ObjectiveModal from '@/components/objectives/ObjectiveModal'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import VirtualizationList from '@/components/VirtualizationList'
import CustomDashboardModuleResolver from '@/views/workspaces/_workspaceId/dashboard/CustomDashboardModuleResolver'
import DashboardAccessDenied from '@/views/workspaces/_workspaceId/dashboard/dashboardAccessDenied'
import ModuleColumnsSettingsModal from '@/views/workspaces/_workspaceId/dashboard/ModuleColumnsSettingsModal'

defineOptions({
  name: 'CustomDashboardItem'
})

const props = defineProps({
  // eslint-disable-next-line vue/no-unused-properties
  workspaceId: {
    type: String,
    default: ''
  },

  type: {
    type: String,
    default: CUSTOM_DASHBOARD_ITEM_TYPES.DEFAULT,
    validator: value => includesValue(value, CUSTOM_DASHBOARD_ITEM_TYPES)
  },

  // hides everything in the header except the add module button
  hideActions: {
    type: Boolean
  },

  hideTitle: {
    type: Boolean
  },

  // element in which cursor moves affect modules drag'n'drop, the dashboard itself if not set
  dragArea: {
    type: HTMLElement,
    default: null
  },

  redirectOnError: {
    type: Boolean,
    default: true
  },

  // enables loading levels for all workspaces from workspaceIds prop
  // disable it when levels are already loaded by parent component
  loadWorkspacesLevels: {
    type: Boolean
  },

  // used to load levels for all passed workspaces
  // required when loadWorkspacesLevels is enabled
  workspaceIds: {
    type: Array,
    default: () => [],
    validator: (value, props) => {
      if (props.loadWorkspacesLevels && (!isArray(value) || isEmpty(value))) {
        throw new Error(
          'CustomDashboardItem: non-empty workspaceIds prop is required when loadWorkspacesLevels is enabled'
        )
      }

      return true
    }
  },

  // dashboard is loaded by group id on homepage type
  groupId: {
    type: [Number, String],
    default: null,
    validator: (value, props) =>
      props.type !== CUSTOM_DASHBOARD_ITEM_TYPES.HOMEPAGE || !isNil(value)
  }
})

const isHomepageType = computed(() => props.type === CUSTOM_DASHBOARD_ITEM_TYPES.HOMEPAGE)

const emit = defineEmits({
  'elements-updated': null
})

const store = useStore()
const router = useRouter()
const route = useRoute()

const objectiveModal = useTemplateRef('objectiveModal')
const resolvedWorkspaceId = ref(null)

const showObjectiveModalOpenForm = ({ element = {}, payload = null, isChildren = false } = {}) => {
  resolvedWorkspaceId.value = element.workspaceId || null
  modalVisibilityState.value[SHOW_OBJECTIVE_MODAL] = true

  nextTick(() => {
    const openMethod = isChildren
      ? objectiveModal.value.openChildForm
      : objectiveModal.value.openForm

    openMethod(
      OKR_TYPE_TO_FORM_VIEW[element.typeId],
      { ...element },
      'custom dashboard',
      true,
      payload
    )
  })
}

const {
  isCheckinModalOpen,
  handleOpenCheckinModal,
  checkinModalLaunchData,
  isShowCheckinModal,
  onCheckinModalClosed
} = useCheckinModal()

const onElementsUpdated = () => {
  reloadDashboard()
  emit('elements-updated')
}

const fullscreenIcon = computed(() => {
  return fullscreen.value ? 'fullscreen-off' : 'fullscreen-on'
})
const onFullscreenClick = () => {
  store.dispatch('system/toggleFullscreen')
}

onBeforeUnmount(() => {
  if (fullscreen.value) {
    store.dispatch('system/toggleFullscreen')
  }
})

const backToCustomDashboardList = () => {
  router.push({ name: ROUTE_NAMES.CUSTOM_DASHBOARD_LIST })
}
const fullscreen = computed(() => store.state.system.fullscreen)
const DEFAULT_FORM_MODEL = {
  name: '',
  backgroundId: 0,
  modules: []
}
const dashboardModel = ref({ ...DEFAULT_FORM_MODEL })

const dashboardItem = computed(() => store.state.dashboard.dashboardItem)

const setDashboardModel = value => {
  dashboardModel.value = cloneDeep(value)
}

watch(
  () => dashboardItem.value,
  val => {
    setDashboardModel(val)
  },
  { deep: true }
)

const favoritesList = computed(() => {
  return (store.state.dashboard.favoriteList || []).reduce((acc, item) => {
    if (item.favorite) {
      return [...acc, item.id]
    }
    return acc
  }, [])
})

watch(
  favoritesList,
  val => {
    if (dashboardModel.value && dashboardModel.value.id) {
      dashboardModel.value.favorite = val.includes(dashboardModel.value.id)
    }
  },
  { deep: true }
)

const updateLevels = async workspaceId => {
  const levelApi = new LevelApiHandler()

  try {
    const levels = await levelApi.getLevelsForFilter({
      workspaceId
    })

    store.dispatch('objectives/setLevels', { levels })
  } catch (error) {
    handleError({ error })
  }
}

watch(
  () => props.workspaceId,
  val => {
    if (!isNull(val) && val) {
      updateLevels(val)
    }
  },
  { immediate: true }
)

if (props.loadWorkspacesLevels) {
  useWorkspacesLevels(() => props.workspaceIds)
}

const handleElementForEditFromQuery = () => {
  if (route) {
    const editValue = getEditValueFromQuery({ query: route.query })

    if (editValue) {
      showObjectiveModalOpenForm({ element: editValue })

      const { children } = editValue

      if (children) {
        showObjectiveModalOpenForm({ element: children, isChildren: true })
      }
    }
  }
}

const loading = ref(true)
const isDashboardUnavailable = ref(false)
const getDashboard = async ({
  withLoader = true,
  isReload = false,
  ignoreCurrentlyEditableModule = false,
  handleRouteQuery = false,
  ignoreModuleParams = false
} = {}) => {
  try {
    if (isReload) {
      customDashboardState.value.isReload = true
    }

    if (withLoader) {
      loading.value = true
    }
    const moduleParams = ignoreModuleParams
      ? []
      : getModulesRequestParams({
          ignoreCurrentlyEditableModule
        })

    if (isHomepageType.value) {
      const groupDashboard = await store.dispatch('dashboard/getDashboardItemByGroupId', {
        groupId: props.groupId,
        moduleParams
      })

      isDashboardUnavailable.value = isNull(groupDashboard)

      if (isDashboardUnavailable.value) {
        return
      }
    } else {
      await store.dispatch('dashboard/getDashboardItem', {
        id: route.params.id,
        moduleParams
      })
    }
    setDashboardModel(dashboardItem.value)

    if (handleRouteQuery) {
      handleElementForEditFromQuery()
    }
  } catch (error) {
    // deleted group answers 404, show it as unavailable dashboard
    if (
      isHomepageType.value &&
      checkResponseStatus({ error, status: RESPONSE_STATUSES.NOT_FOUND })
    ) {
      isDashboardUnavailable.value = true
      return
    }

    if (props.redirectOnError) {
      if (checkResponseStatus({ error, status: RESPONSE_STATUSES.FORBIDDEN })) {
        await router.push({ name: ROUTE_NAMES.CUSTOM_DASHBOARD_ACCESS_DENIED })
      } else {
        await router.push({ name: ROUTE_NAMES.MISSING_PAGE })
      }
    }
    handleError({ error })
  } finally {
    loading.value = false

    if (customDashboardState.value.isReload) {
      customDashboardState.value.isReload = false
    }
  }
}

const reloadDashboard = async ({ ignoreCurrentlyEditableModule = false } = {}) => {
  await getDashboard({ withLoader: false, isReload: true, ignoreCurrentlyEditableModule })
}

onMounted(async () => {
  await getDashboard({
    handleRouteQuery: true
  })
})

watch(
  () => route.params.id,
  val => {
    if (val && !isHomepageType.value) {
      getDashboard({ ignoreModuleParams: true })
    }
  }
)

watch(
  () => props.groupId,
  val => {
    if (!isNil(val) && isHomepageType.value) {
      getDashboard({ ignoreModuleParams: true })
    }
  }
)
const SHOW_EDIT_MODULE_MODAL = 'SHOW_EDIT_MODULE_MODAL'
const SHOW_ADD_OBJECTIVES_MODAL = 'SHOW_ADD_OBJECTIVES_MODAL'
const SHOW_DUPLICATE_MODAL = 'SHOW_DUPLICATE_MODAL'
const SHOW_CONFIRM_DELETE_DIALOG = 'SHOW_CONFIRM_DELETE_DIALOG'
const SHOW_CONFIRM_DELETE_MODULE_DIALOG = 'SHOW_CONFIRM_DELETE_MODULE_DIALOG'
const SHOW_SHARING_MODAL = 'SHOW_SHARING_MODAL'
const SHOW_CHOOSE_MODULE_DIALOG = 'SHOW_CHOOSE_MODULE_DIALOG'
const SHOW_OBJECTIVE_MODAL = 'SHOW_OBJECTIVE_MODAL'
const modalVisibilityState = ref({
  [SHOW_EDIT_MODULE_MODAL]: false,
  [SHOW_ADD_OBJECTIVES_MODAL]: false,
  [SHOW_DUPLICATE_MODAL]: false,
  [SHOW_CONFIRM_DELETE_DIALOG]: false,
  [SHOW_CONFIRM_DELETE_MODULE_DIALOG]: false,
  [SHOW_SHARING_MODAL]: false,
  [SHOW_CHOOSE_MODULE_DIALOG]: false,
  [SHOW_OBJECTIVE_MODAL]: false
})

const isNoModules = computed(() => {
  return isEmpty(dashboardModel.value.modules) && !loading.value
})

const moduleToManage = computed(() => {
  if (!moduleIdToManage.value) {
    return {}
  }
  return dashboardModel.value.modules.find(item => item.id === moduleIdToManage.value) || {}
})

const showModal = (modalName, id) => {
  moduleIdToManage.value = id
  modalVisibilityState.value[modalName] = true
}

const hideModal = modalName => {
  modalVisibilityState.value[modalName] = false
}

const onSuccessRemoveDashboard = () => {
  router.push({ name: ROUTE_NAMES.CUSTOM_DASHBOARD_LIST })
  if (dashboardModel.value.favorite) {
    store.dispatch('dashboard/getFavoriteList')
  }
}

const getOwner = computed(() => {
  const { ownerAvatarUrl, ownerId, ownerName } = dashboardItem.value

  return {
    ownerAvatarUrl,
    ownerId,
    ownerName
  }
})

const isLoadingFavoriteToggle = ref(false)
const toggleFavorite = async () => {
  try {
    isLoadingFavoriteToggle.value = true
    await store.dispatch('dashboard/toggleFavorite', dashboardModel.value)
  } catch (error) {
    handleError({ error })
  } finally {
    isLoadingFavoriteToggle.value = false
  }
}
const { isOwner, isPluginAdmin, isEditor } = getDashboardState()

const moduleIdToManage = ref(null)

const isTitleEmpty = ref(false)

const validateName = () => {
  isTitleEmpty.value = isStringEmpty(dashboardModel.value.name)
}

const onNameUpdate = async () => {
  if (dashboardItem.value.name === dashboardModel.value.name) return

  validateName()
  if (isTitleEmpty.value) return

  try {
    const customDashboardApi = new CustomDashboardApiHandler()
    await customDashboardApi.updateDashboard({
      ...dashboardModel.value,
      id: dashboardModel.value.id,
      name: dashboardModel.value.name,
      isPrivate: dashboardModel.value.private
    })

    await reloadDashboard()
  } catch (error) {
    handleError({ error })
  }
}

const scrollYPosition = shallowRef(0)

const onDragStart = () => {
  scrollYPosition.value = window.scrollY

  customDashboardState.value.isDragging = true
}

const onDragEnd = async ({ list, from: { index: oldIndex, item }, to: { index: newIndex } }) => {
  customDashboardState.value.isDragging = false

  await nextTick()
  window.getSelection().removeAllRanges()
  if (scrollYPosition.value) {
    window.scrollTo(0, scrollYPosition.value)
  }

  const { orderValue } = handleOrder({
    oldIndex,
    newIndex,
    data: list
  })

  try {
    const customDashboardApi = new CustomDashboardApiHandler()

    await customDashboardApi.setOrderForModule({
      orderValue,
      dashboardId: dashboardModel.value.id,
      moduleId: item.id
    })
    await reloadDashboard()
  } catch (error) {
    handleError({ error })
  }
}

const isModuleLimitReached = computed(() => dashboardItem.value.modules.length >= MODULE_LIMIT)

const userCanManageCustomDashboard = computed(
  () => isOwner.value || isPluginAdmin.value || isEditor.value
)

const toggleModuleColumnsSettings = (module = {}) => {
  if (isEmpty(module)) {
    customDashboardState.value.columnsSettingsModuleId = null
    customDashboardState.value.enabledColumns = []
    return
  }
  customDashboardState.value.columnsSettingsModuleId = module.id
  customDashboardState.value.enabledColumns = module.enabledColumns
}

const updateModuleData = data => {
  if (!dashboardModel.value.modules) return

  dashboardModel.value.modules = dashboardModel.value.modules.map(module => {
    if (module.id === data.id) {
      return { ...module, ...data }
    }
    return module
  })
}

const { customDashboardModulesState, modulesReferenceKey, modulesReference, exportModuleToPdf } =
  useCustomDashboardModuleHelper()

const pageWrapper = useTemplateRef('pageWrapperRef')

useVerticalDragLock({
  containerReference: modulesReference,
  areaReference: () => props.dragArea || pageWrapper.value,
  itemSelector: '.cdi-ModuleItem'
})

const getModulesRequestParams = ({ ignoreCurrentlyEditableModule = false } = {}) => {
  return Object.entries(customDashboardModulesState.value).reduce((acc, [id, module]) => {
    const moduleId = Number(id)

    const isCurrentModuleCurrentlyEditable = moduleId === moduleIdToManage.value

    const shouldIgnoreModule = ignoreCurrentlyEditableModule && isCurrentModuleCurrentlyEditable

    if (
      !Number.isNaN(moduleId) &&
      // for now, we need to get to request params only for Progress modules
      module.typeId === MODULE_TYPE_IDS.KPI &&
      // here we remove the moduleId that we are going to edit
      !shouldIgnoreModule
    ) {
      const { currentGranulationId: granulationId, viewPointLastDate, columnCount } = module
      acc.push({
        moduleId,
        granulationId,
        viewPointLastDate,
        columnCount
      })
    }
    return acc
  }, [])
}
provide(customDashboardModulesStateInjectionKey, customDashboardModulesState)

const customDashboardState = ref({
  toggleModuleColumnsSettings,
  userCanManageCustomDashboard,
  columnsSettingsModuleId: null,
  enabledColumns: [],
  editElement: showObjectiveModalOpenForm,
  openCheckinModal: handleOpenCheckinModal,
  addElementsToModule: ({ id }) => showModal(SHOW_ADD_OBJECTIVES_MODAL, id),
  duplicateModule: ({ id }) => showModal(SHOW_DUPLICATE_MODAL, id),
  removeModule: ({ id }) => showModal(SHOW_CONFIRM_DELETE_MODULE_DIALOG, id),
  editModule: ({ id }) => showModal(SHOW_EDIT_MODULE_MODAL, id),
  updateModuleData: updateModuleData,
  reloadDashboard,
  onElementPeriodThresholdUpdated: reloadDashboard,
  onJiraIssueProgressUpdated: reloadDashboard,
  exportModule: exportModuleToPdf,
  isReload: false,
  isReadMode: false,
  isDragging: false,
  showLastComment: true
})

provide(customDashboardStateInjectionKey, customDashboardState)

const updateModuleEnabledColumns = async ({ moduleId, enabledColumns = [] } = {}) => {
  if (!moduleId) return

  try {
    const api = new CustomDashboardApiHandler()
    await api.updateModuleAttribute({
      activeColumns: enabledColumns,
      moduleId: moduleId,
      id: dashboardModel.value.id
    })

    const targetModule = dashboardModel.value.modules.find(module => module.id === moduleId)

    if (targetModule) {
      targetModule.enabledColumns = enabledColumns
    }

    customDashboardState.value.toggleModuleColumnsSettings()
  } catch (error) {
    handleError({ error })
  }
}

const onConfirmDuplicate = async ({ newModuleId }) => {
  await reloadDashboard()
  await nextTick(() => {
    const sectionElement = document.getElementById(getCustomDashboardModuleSelectorId(newModuleId))
    if (sectionElement) {
      sectionElement.scrollIntoView()
    }
  })
}

const getCustomDashboardModuleTestId = ({ typeId, id }) => {
  const moduleNames = {
    [MODULE_TYPE_IDS.OKR]: 'okr',
    [MODULE_TYPE_IDS.KPI]: 'kpi',
    [MODULE_TYPE_IDS.TEXT]: 'text'
  }
  return `${moduleNames[typeId] || 'unknown'}-module-${id}`
}

defineExpose({
  reloadDashboard
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/_custom-dashboard';
@import '~@/assets/styles/_custom-dashboard-animations';

$customBackground: 'https://s3-oboard-public-static.s3.eu-central-1.amazonaws.com/img/dashboard_map.png';

.cdi-Header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--ob-cdi-header-margin-bottom, 56px);
  gap: 16px;
  padding-left: $page-left-padding;
  padding-right: var(--ob-cdi-page-right-padding, #{$page-right-padding});
}
.cdi-Header_Right {
  display: flex;
  align-items: center;
  gap: 16px;
}
.cdi-Header-Button {
  color: $dark-2;
  font-weight: fw('medium');
  font-size: $fs-16;
  line-height: 24px;
  padding: 0;
  &-lightGrey {
    color: $grey-1-next;
  }
}
.cdi-PageWrapper {
  //padding-left: $page-left-padding;
  //padding-right: $page-right-padding;
  padding: var(--ob-cdi-page-top-padding, calc(#{$page-top-padding} + 4px)) 0 0 0;
  background: var(--ob-cdi-page-background, url($customBackground) top/100% no-repeat);

  &-is-dragging {
    user-select: none;

    > * {
      user-select: none;
    }
  }
}
.cdi-ButtonFavorite-active {
  @extend %energyPop;
}

.cdi-Header_Left {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 8px;
}

.cdi-BreadcrumbCurrentName {
  font-weight: fw('medium');
  font-size: $fs-16;
  line-height: 24px;
  color: $dark-1;
}

.cdi-Title {
  font-size: $fs-32;
  line-height: 48px;
  word-break: break-all;
  font-weight: fw('semi-bold');
  margin-bottom: 32px;
  padding-left: $page-left-padding;
  padding-right: $page-right-padding;
}
.cdi-ModuleWrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: var(--ob-cdi-page-right-padding, #{$page-right-padding});

  &-is-dragging {
    user-select: none;
  }

  &-no-edit,
  &-no-modules {
    padding-left: var(--ob-cdi-page-left-padding, #{$page-left-padding});
  }
}
</style>
<style lang="scss">
@import '~@/assets/styles/dnd';

.cdi-PageWrapper {
  .sortable-chosen,
  .original-chosen {
    .cdm-WrapperContent,
    .cdm-CollapseIcon,
    .oma-Content,
    .tma-Actions,
    .cdpm-Actions {
      display: none;
    }
    .cdm-Header {
      border-bottom: none;
    }
    .cdt-TitleWrapper {
      display: none;
    }
    .cdt-Title {
      display: block !important;
    }
  }

  .sortable-chosen,
  .original-chosen {
    .cdm-DragItem {
      opacity: 1 !important;
    }

    .cdm-Wrapper {
      box-shadow: $common-box-shadow;

      position: relative;

      @extend %grabbing-item-common-styles;
      @include grabbingItemBackdrop();
    }
  }
}

.cdi-ModuleItem {
  &:not(:last-child) {
    margin-bottom: 16px;
  }
}

.cdi-EmptyState {
  width: 100%;
  border-radius: $border-radius-lg;
  background: $white;
  border: 1px solid $grey-2-next;
}
</style>
